import { movies } from '@/mocks/movies';
import { topics } from '@/mocks/topics';
import type { MediaType, Movie } from '@/types/movie';

export { movies, topics };

export function getMovieById(id: string): Movie | undefined {
  return movies.find((m) => m.id === id);
}

export function getMoviesByIds(ids: string[]): Movie[] {
  return ids.map((id) => getMovieById(id)).filter((m): m is Movie => Boolean(m));
}

export function getMoviesByType(type: MediaType): Movie[] {
  return movies.filter((m) => m.type === type);
}

/** 最近更新优先 */
export function getLatestMovies(limit = 8, type?: MediaType): Movie[] {
  const list = type ? getMoviesByType(type) : movies;
  return [...list].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)).slice(0, limit);
}

/** 热度优先 */
export function getHotMovies(limit = 8): Movie[] {
  return [...movies].sort((a, b) => b.heat - a.heat).slice(0, limit);
}

export function getTopicById(id: string) {
  return topics.find((t) => t.id === id);
}

export interface FilterOptions {
  keyword?: string;
  type?: MediaType | 'all';
  genres?: string[];
  regions?: string[];
  years?: { min: number; max: number }[];
  sort?: 'hot' | 'latest' | 'rating';
}

export function filterMovies(options: FilterOptions): Movie[] {
  const { keyword, type = 'all', genres = [], regions = [], years = [], sort = 'hot' } = options;
  let list = movies.filter((m) => {
    if (type !== 'all' && m.type !== type) return false;
    if (genres.length && !m.genres.some((g) => genres.includes(g))) return false;
    if (regions.length && !regions.includes(m.region)) return false;
    if (years.length && !years.some((y) => m.year >= y.min && m.year <= y.max)) return false;
    if (keyword && keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      const haystack = `${m.title} ${m.director} ${m.actors.join(' ')} ${m.genres.join(' ')} ${m.summary}`.toLowerCase();
      if (!haystack.includes(kw)) return false;
    }
    return true;
  });

  if (sort === 'latest') {
    list = [...list].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  } else if (sort === 'rating') {
    list = [...list].sort((a, b) => b.rating - a.rating);
  } else {
    list = [...list].sort((a, b) => b.heat - a.heat);
  }
  return list;
}

/** 基于类型与关键词的关联推荐 */
export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  return movies
    .filter((m) => m.id !== movie.id)
    .map((m) => {
      const genreScore = m.genres.filter((g) => movie.genres.includes(g)).length;
      const sameType = m.type === movie.type ? 1 : 0;
      const sameRegion = m.region === movie.region ? 1 : 0;
      return { m, score: genreScore * 2 + sameType + sameRegion };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || b.m.heat - a.m.heat)
    .slice(0, limit)
    .map((x) => x.m);
}